/* jshint esversion: 6 */
import React from 'react';
import PropTypes from 'prop-types';
import AutomateHeader from 'app/components/Headers/AutomateHeader.jsx';
import ConfirmModal from 'app/components/common/ConfirmModal.jsx';
import ConfirmPostRecentModal from 'app/components/common/ConfirmPostRecentModal.jsx';
import Content from 'app/components/common/Content.jsx';
import Loading from 'app/components/common/Loading.jsx';
import RouteListComponent from 'app/components/RouteList/RouteListComponent.jsx';
import reportInputRefresh from 'app/modules/reportInputRefresh.js';
import UserMessages from 'app/modules/UserMessageModule.js';
import * as _ from 'underscore';
import ComponentWithContext from 'app/components/Routers/ComponentWithContext.jsx';
import { SOCIAL_CREATE, SOCIAL_UPDATE } from 'app/redux/constants.js';

class AutomateView extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			newSocial: null,
			routeCounts: {},
			reauthorizing: false,
		};

		this.reauthInput = this.reauthInput.bind(this);
		this.createNewRoute = this.createNewRoute.bind(this);
		this.postRecentQuiz = this.postRecentQuiz.bind(this);
		this.setMasterTwitterRole = this.setMasterTwitterRole.bind(this);
		this.setNewSocial = this.setNewSocial.bind(this);
		this.onSocialCreated = this.onSocialCreated.bind(this);
		this.onSocialUpdated = this.onSocialUpdated.bind(this);

		// Held rather than rebuilt: unregistering matches the object that registered.
		this.socialCallbacks = [
			{action: SOCIAL_CREATE, func: this.onSocialCreated},
			{action: SOCIAL_UPDATE, func: this.onSocialUpdated}
		];

	}

	componentDidMount() {

		let routeID = this.props.router.params.routeId,
			redirect = '/share/automate';

		if (routeID) {
			redirect = '/share/automate/detail/' + this.props.router.params.routeId;
		}

		this.props.getRoutes();
		this.props.getSocials();

		this.props.remoteReturnHandler([{
			method: 'POST',
			path: redirect,
			callback: (social) => {
				this.props.resetAuthState();
				this.setState({
					newSocial: social
				});
			}
		},
		{
			method: 'INPUT',
			path: redirect,
			callback: this.reauthInput
		},
		/*
		 * The trailing underscore is load-bearing: these are substring matches
		 * against the auth state, and a bare 'REAUTH' also matches the
		 * 'REAUTHINPUT_' the entry above is here for.
		 *
		 * No callback needed — the handler updates the social itself, and the
		 * SOCIAL_UPDATE registration below picks the response up. What this entry
		 * is really for is `path`, which is navigated to with replace: true and so
		 * takes the spent ?state= off the URL. Left on, a reload re-runs the
		 * reconnect against a credential the provider has already handed back.
		 */
		{
			method: 'REAUTH_',
			path: redirect,
			callback: null
		}]);

		this.socialCallbacks.map((callback) => { this.props.registerCallback(callback); });

	}

	componentWillUnmount() {
		this.socialCallbacks.map((callback) => { this.props.unregisterCallback(callback); });
	}

	/*
	 * A social was just connected from this page. Which one is the whole question,
	 * and the registry answers it — this used to be reconstructed here by diffing
	 * the id lists either side of the response.
	 *
	 * `newSocial` is set by the remote-return handler when the connection was
	 * started to fill an output on the route being looked at, and it is what
	 * decides between the two endings: finish that output, or offer the account
	 * the feeds it could post to.
	 */
	onSocialCreated(detail) {
		let output = _.clone(this.state.newSocial),
			constants = this.props.app.config.constants,
			social = detail.social;

		if (!social) {
			return;
		}

		if ([constants.POST_SVC_INSTAGRAM_BUSINESS, constants.POST_SVC_FACEBOOK, constants.POST_SVC_TIKTOK].indexOf(social.psID) !== -1) {
			this.props.getSocialInputCreds();
		}

		/*
		 * This flow attaches the new social to the route the user is already on
		 * (postRecentQuiz, below), so the picker only makes sense when there is no
		 * output waiting to be created — otherwise it stacks on top of the
		 * post-recent prompt and offers the very route the social is about to be
		 * added to.
		 */
		if (!output && this.props.userHasFeeds()) {
			this.props.showSuggestFeedsModal(social.id);
		}

		if (output) {
			output.paID = social.id;

			this.setState({
				newSocial: null
			});

			this.postRecentQuiz(output);
		}

	}

	/*
	 * Every social write this page sees lands here. All of them can change what
	 * is left on the plan, so the perms are refreshed first; the rest is only
	 * for a reconnect that started on this page and has come back. Reactivating
	 * an account and the role toggles in the route detail go through the same
	 * action, so the auth state is what separates them: it is only set while a
	 * connect return is being handled, and only the REAUTH_ form is a social
	 * being re-authorised.
	 *
	 * The errors themselves are componentDidUpdate's to answer — it raises the
	 * matching prompt. What this does on a rejection is decide whether the
	 * reconnect is over: a duplicate can still be confirmed into a move, which
	 * re-issues the update and lands back here with the auth state set, so that
	 * one keeps it. Anything else is final and releases it.
	 */
	onSocialUpdated() {
		let credID = this.props.authState.credID;

		/*
		 * Refreshed here rather than in the route list, which used to do it: the
		 * list only mounts once ROUTE_LIST has landed, and a reconnect return
		 * fires its update at mount, alongside that fetch. This page is up for
		 * the whole visit.
		 */
		this.props.getUserPerms();

		if (!credID || !credID.match(/^REAUTH_/)) {
			return;
		}

		if (this.props.getApiErrors(['SOCIAL_UPDATE']).length) {
			/*
			 * Released here rather than by whichever prompt gets dismissed: the
			 * next SOCIAL_UPDATE request clears these errors on its way out (app
			 * reducer), so a REAUTH_ left behind would make that unrelated update
			 * read as this reconnect succeeding.
			 */
			if (!this.props.isDuplicateSocial()) {
				this.props.resetAuthState();
			}

			return;
		}

		this.props.resetAuthState();
		this.props.addMessage(UserMessages.generateMessage(UserMessages.social.reauthorized, 'SOCIAL_UPDATE'));

	}

	componentDidUpdate(prevProps, prevState) {
		let errors = [],
			modalAdded = this.state.modalAdded;

		const clearSocialErrors = () => {
			this.props.deleteApiError(['SOCIAL_CREATE', 'SOCIAL_UPDATE']);
		};

		/*
		 * Declining the move leaves the account unreconnected, so the auth state
		 * has to go with the error — onSocialUpdated reads it as a reconnect still
		 * in flight, and would report the next unrelated social update as one.
		 */
		const abandonSocialConnect = () => {
			clearSocialErrors();
			this.props.resetAuthState();
		};

		// a route was just deleted (deletion happens in SimpleRouteItemDetail)
		if (prevProps.automatedRoutes.list.length > this.props.automatedRoutes.list.length) {
			this.props.closeAllModals();

		} else if (prevProps.automatedRoutes.list.length < this.props.automatedRoutes.list.length && prevProps.app.loading.ROUTE_LIST === false && this.props.app.loading.ROUTE_LIST === false) {
			// new route will have been pushed onto the end of the route array
			let newRoute = this.props.automatedRoutes.list[this.props.automatedRoutes.list.length - 1];
			this.props.router.navigate('/share/automate/detail/' + newRoute.id);
		}

		// check inputsAvailable because a user may have spent all their feeds in curation
		if (prevProps.app.loading.ROUTE_LIST === true &&
			this.props.app.loading.ROUTE_LIST === false &&
			this.props.automatedRoutes.list.length === 0 &&
			parseInt(this.props.user.perms.resources.inputsAvailable) > 0) {
			let onboarding = (typeof(Storage) !== undefined) ? JSON.parse(window.localStorage.getItem('onboarding')) : null;
			if (!this.props.isFreeUser() && onboarding !== null && this.props.getPersistedData("newRouteState") === null) {
				this.props.router.navigate('/welcome');
			} else {
				this.props.router.navigate('/share/automate/new/inputs');
			}
			//this.props.router.navigate('/share/automations');
		}

		if (prevState.reauthorizing === true && this.state.reauthorizing === true) {
			this.props.addMessage(UserMessages.generateMessage(UserMessages.input.reauthorized, 'INPUT_UPDATE'));
			this.setState({
				reauthorizing: false
			});
		}

		if (prevProps.app.loading.INPUT_REFRESH === true && this.props.app.loading.INPUT_REFRESH === false) {
			reportInputRefresh(this.props);
		}

		// check for and handle social creation errors
		if (this.props.isDuplicateSocial() && this.props.isDuplicateSocialModalVisible === false) {
			let social = null;
			if (this.state.newSocial !== null) {
				social = _.clone(this.state.newSocial);
			} else {
				for (let i = 0; i < this.props.app.errors.api.length; ++i) {
					let error = this.props.app.errors.api[i];
					if (['SOCIAL_CREATE', 'SOCIAL_UPDATE'].indexOf(error.type) !== -1 && error.post !== undefined) {
						social = _.clone(error.post);
					}
				}
			}
			this.props.showDuplicateSocialModal(social, {
				confirm: clearSocialErrors,
				cancel: abandonSocialConnect
			});
		} else if (this.props.isBlacklisted() && this.props.isBlacklistModalVisible === false) {
			this.props.showBlacklistModal({
				confirm: clearSocialErrors,
				cancel: clearSocialErrors
			});
		} else if (this.props.isMovementRestricted() && this.props.isMovementRestrictedModalVisible === false) {
			this.props.showMovementRestrictedModal({
				confirm: clearSocialErrors,
				cancel: clearSocialErrors
			});
		}else if (this.props.isOverTheLimit() && this.props.isUpgradeModalVisible === false) {
			/*
			 * Cleared on close, or the guard above re-raises this the moment it
			 * is dismissed: isOverTheLimit() reads this same error and nothing in
			 * the modal's close path removes it. Fetched fresh because the
			 * `errors` declared above is still [] here — it is only assigned from
			 * the OUTPUT_* errors further down.
			 */
			let socialErrors = this.props.getApiErrors(['SOCIAL_CREATE', 'SOCIAL_UPDATE']);

			this.props.showUpgradeModal(() => {
				clearSocialErrors();
				this.props.closeAllModals();
			}, null, socialErrors, 'automate_error');
		}

		errors = this.props.getApiErrors(['OUTPUT_CREATE', 'OUTPUT_UPDATE']);
		if (errors.length) {
			let showTwitterError = false;

			let validation = errors[0].errors?.validation;

			if ((validation?.paID !== undefined && validation.paID.match("Twitter outputs")) ||
				(validation?.activeFlag !== undefined && validation.activeFlag.match("Twitter outputs"))) {
				showTwitterError = true;
			}

			if (showTwitterError) {
				this.props.addModal(
					<ConfirmModal
						{...this.props}
						modalCopy={"Only a single Twitter output is allowed in this type of route"}
						cancelAction={() => {
							this.props.deleteApiError(['OUTPUT_CREATE', 'OUTPUT_UPDATE']);
							this.props.closeAllModals();
						}}
						confirmAction={() => {
							this.props.deleteApiError(['OUTPUT_CREATE', 'OUTPUT_UPDATE']);
							this.props.closeAllModals();
						}}
						showCancel={false}
						confirmLabel={"OK"}/>
				);

				this.props.deleteApiError(['OUTPUT_CREATE', 'OUTPUT_UPDATE']);
			}

		}

		errors = this.props.getApiErrors(['INPUT_CREATE']);
		if (errors.length) {
			this.props.showUpgradeModal(this.props.closeAllModals, null, errors[0], 'automate_error');
			this.props.deleteApiError(['INPUT_CREATE']);
		}

	}

	reauthInput(input) {

		this.props.resetAuthState();
		input.routeId = this.props.router.params.routeId;
		this.props.updateInput(input);
		this.setState({
			reauthorizing: true
		});

	}

	createNewRoute(e){

		e.preventDefault();
		let trigger = 'automate-footer';

		if (e.target.className.match('new-icon')) {
			trigger = 'automate-banner';
		}

		if (this.props.user.perms.resources.inputsAvailable === 0) {
			this.props.showUpgradeModal(this.props.closeAllModals, 'inputs', null, trigger);
		} else {
			this.props.router.navigate('/share/automate/new/inputs');
		}

	}

	setMasterTwitterRole(output) {
		let constants = this.props.app.config.constants,
			route = null,
			twitterCount = 0;

		output.role = constants.OR_ROLE_RETWEET;

		for (var i = 0; i < this.props.automatedRoutes.list.length; ++i) {
			if (this.props.automatedRoutes.list[i].id == output.rID) {
				route = this.props.automatedRoutes.list[i];
				break;
			}
		}

		if (route) {
			for (i = 0; i < route.outputs.length; ++i) {
				if (route.outputs[i].social.psID == constants.POST_SVC_TWITTER) {
					++twitterCount;
					break;
				}
			}
		}

		if (twitterCount == 0) {
			output.role = constants.OR_ROLE_NORMAL;
		}

		return output;

	}

	postRecentQuiz(output) {
		let constants = this.props.app.config.constants;

		// if the paID is not defined and a new social is present assign it
		if (!output.paID && this.state.newSocial) {
			output.paID = this.state.newSocial;
			this.setState({
				newSocial: 0
			});
		}

		if (parseInt(output.psID, 10) === constants.POST_SVC_TWITTER) {
			output = this.setMasterTwitterRole(output);
		}

		let cancelConfirm = () => {
				this.props.createOutput(output);
				this.props.closeAllModals(null);
			},
			confirmConfirm = () => {
				output.postRecent = 1;
				this.props.createOutput(output);
				this.props.closeAllModals(null);
			},
			message = 'Would you like to share the most recent item to this social?',
			modal = (
				<ConfirmPostRecentModal
					modalCopy={message}
					cancelAction={cancelConfirm}
					confirmAction={confirmConfirm} />
			);

		this.props.addModal(modal);

	}

	setNewSocial(newSocial) {

		this.setState({
			newSocial
		});

	}

	render() {
		let RouteList = null,
			Header = (
				<AutomateHeader
					{...this.props}
					recordPath={this.props.recordPath}
					getSavedPath={this.props.getSavedPath}
					newRouteHandler={this.createNewRoute} />
			),
			outputId = 0,
			routeId = 0,
			route = null,
			routeName = "",
			constants = this.props.app.config.constants,
			type = constants.INPUT_TYPE_FEED;

		if (this.props.app.loading.ROUTE_LIST) {
			RouteList = <Loading/>;
		} else {
			RouteList = (
				<RouteListComponent
					{...this.props}
					newRouteInfo={this.state.newRouteInfo}
					recordPath={this.props.recordPath}
					newRouteHandler={this.createNewRoute}
					setNewSocial={this.setNewSocial} />
			);
		}

		return (
			<Content
				{...this.props}
				header={Header}
				main={RouteList} />
		);
	}

};

AutomateView.displayName = "Automate";

AutomateView.propTypes = {
	routeViewType: PropTypes.number,
	recordPath: PropTypes.func
};

AutomateView.defaultProps = {
	routeViewType: 0, // ROUTE_VIEW_ADVANCED
	recordPath: () => {}
};

export default ComponentWithContext(AutomateView);
